// libs/ui/src/primitives/Card.tsx
import { useCallback, type ComponentProps } from 'react';
import { styled, withStaticProperties, XStack, YStack } from 'tamagui';
import { dropShadow } from '../theme/shadowStyles';

const CardFrame = styled(YStack, {
  name: 'Card',
  overflow: 'hidden',

  variants: {
    variant: {
      flat: {
        backgroundColor: '$surface',
      },
      elevated: {
        backgroundColor: '$surface',
        ...dropShadow({ offsetY: 2, radius: 8, opacity: 1, color: '$polaroidShadow', elevation: 3 }),
      },
      pressable: {
        backgroundColor: '$surface',
        borderWidth: 1,
        borderColor: '$borderDefault',
        ...dropShadow({ offsetY: 2, radius: 8, opacity: 1, color: '$polaroidShadow', elevation: 3 }),
        transition: 'quicker',
        // Press feedback: subtle scale + surface dim. Hover lift only on web.
        pressStyle: { backgroundColor: '$surfacePressed', scale: 0.985 },
        hoverStyle: { scale: 1.005 },
        focusVisibleStyle: {
          outlineColor: '$focusRingColor',
          outlineWidth: 2,
          outlineStyle: 'solid' as never,
          outlineOffset: 2,
        },
        // a11y — keyboard activates the press. role=button announced by
        // RN-web; on native, the Pressable wrapper above the card sets
        // the accessibilityRole.
        accessibilityRole: 'button' as never,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cursor: 'pointer' as any,
        // Web-only tab focus.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tabIndex: 0 as any,
      },
      polaroid: {
        backgroundColor: '$polaroidFrame',
        padding: 2,
        ...dropShadow({ offsetY: 4, radius: 12, opacity: 1, color: '$polaroidShadow', elevation: 4 }),
      },
    },
    // Boolean compositional variant — dims the card, blocks press
    // feedback, and drops the card out of the keyboard tab order so it
    // can't be activated by Enter / Space. The `pressable` variant
    // declares `tabIndex: 0`; this override wins because styled
    // variants apply in the order declared above (variant first,
    // then disabled).
    disabled: {
      true: {
        opacity: 0.5,
        pressStyle: undefined as never,
        hoverStyle: undefined as never,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tabIndex: -1 as any,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        cursor: 'not-allowed' as any,
        'aria-disabled': true as never,
      },
    },
  } as const,

  defaultVariants: {
    variant: 'flat',
  },
});

// RN-Web threads `onKeyDown` through to the underlying div at runtime even
// though Tamagui's view types don't declare it. Extending the props locally
// keeps the destructure typed without losing the web behavior.
type CardKeyEvent = { nativeEvent?: { key?: string }; preventDefault?: () => void };
type CardFrameProps = ComponentProps<typeof CardFrame> & {
  onKeyDown?: (e: CardKeyEvent) => void;
};

/**
 * Wraps the styled CardFrame and wires Enter/Space → onPress for the
 * pressable variant. RN-web's div surface doesn't fire onPress for
 * keyboard activation by default — the tabIndex + role=button set in
 * the styled variant get the focus into the card, this handler closes
 * the loop so keyboard users can activate it. ADR-0077.
 */
function CardComponent({ onKeyDown, onPress, ...rest }: CardFrameProps) {
  const isDisabled = rest.disabled === true;
  const handleKeyDown = useCallback(
    (e: CardKeyEvent) => {
      const key = e?.nativeEvent?.key ?? '';
      if (
        rest.variant === 'pressable' &&
        onPress &&
        !isDisabled &&
        (key === 'Enter' || key === ' ')
      ) {
        e?.preventDefault?.();
        onPress(e as never);
      }
      onKeyDown?.(e);
    },
    [onKeyDown, onPress, rest.variant, isDisabled],
  );

  // Tamagui's view types don't expose onKeyDown; assert via Record to thread
  // the prop through to RN-Web's div surface without breaking typed callers.
  const keyDownProps = { onKeyDown: handleKeyDown } as unknown as Partial<ComponentProps<typeof CardFrame>>;
  return (
    <CardFrame
      // Drop onPress entirely when disabled so mouse clicks no longer
      // activate the card — visual dim alone is insufficient.
      {...(isDisabled ? {} : { onPress })}
      {...keyDownProps}
      {...rest}
    />
  );
}

const CardHeader = styled(XStack, {
  name: 'Card.Header',
  backgroundColor: '$polaroidCaptionBg',
  paddingHorizontal: '$2',
  paddingVertical: '$2',
  alignItems: 'center',
  gap: '$2',
});

const CardBody = styled(YStack, {
  name: 'Card.Body',
  flex: 1,
  padding: '$3',
});

const CardImage = styled(YStack, {
  name: 'Card.Image',
  overflow: 'hidden',
  position: 'relative',
});

export const Card = withStaticProperties(CardComponent, {
  Header: CardHeader,
  Body: CardBody,
  Image: CardImage,
});
